import { ArrowBack } from "@mui/icons-material";
import { Box, Button, CircularProgress, Divider, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { RefreshData } from "components/helper";
import { MaterialReactTable, useMaterialReactTable } from "material-react-table";
import { useMemo, useState } from "react";
import { FaDownload } from "react-icons/fa";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { getMrtBodyRowSx, getMrtTableThemeProps } from "theme/mrtTableTheme";
import { fetchAllAdmissionsForExport } from "views/admin/Admissions/admissionListApi";
import StatusBreakdown from "./StatusBreakdown";
import SummaryCards from "./SummaryCards";
import { downloadAdmissionsExportCsv } from "./admissionsExportCsv";
import { downloadSummaryCsv } from "./summaryCsv";
import { completionRate, formatCount, formatDate } from "./summaryConstants";
import { fetchUniversityAdmissionSummary } from "./universitySummaryApi";
import { IoArrowBack } from "react-icons/io5";

const CountCell = ({ value, color }) => (
  <Typography component="span" sx={{ fontWeight: 600, color: color || "text.primary" }}>
    {formatCount(value)}
  </Typography>
);

const AdmissionSummaryReport = () => {
  const navigate = useNavigate();
  const [isExportingAdmissions, setIsExportingAdmissions] = useState(false);

  const exportAllAdmissions = async () => {
    setIsExportingAdmissions(true);
    try {
      const admissions = await fetchAllAdmissionsForExport();
      downloadAdmissionsExportCsv(admissions);
    } catch (err) {
      toast.error(err?.response?.data?.errorMessage || "Unable to export admissions");
    } finally {
      setIsExportingAdmissions(false);
    }
  };

  const reportQuery = useQuery(["university-admission-summary"], fetchUniversityAdmissionSummary, {
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    staleTime: Infinity,
    cacheTime: Infinity,
  });

  const rows = reportQuery.data?.universities || [];
  const totals = reportQuery.data?.totals || null;

  const columns = useMemo(
    () => [
      { accessorKey: "name", header: "University/College", size: 240 },
      { accessorKey: "code", header: "CRICOS/RTO Code", size: 130 },
      { accessorKey: "state", header: "State", size: 140 },
      {
        accessorKey: "totalAdmissions",
        header: "Admissions",
        size: 110,
        Cell: ({ row }) => <CountCell value={row.original.totalAdmissions} />,
      },
      {
        accessorKey: "completed",
        header: "Completed",
        size: 110,
        Cell: ({ row }) => <CountCell value={row.original.completed} color="success.main" />,
      },
      {
        accessorKey: "inProgress",
        header: "In Progress",
        size: 110,
        Cell: ({ row }) => <CountCell value={row.original.inProgress} color="warning.main" />,
      },
      {
        accessorKey: "withdrawn",
        header: "Withdrawn",
        size: 110,
        Cell: ({ row }) => <CountCell value={row.original.withdrawn} color="error.main" />,
      },
      {
        id: "completionRate",
        header: "Completion",
        size: 110,
        accessorFn: (row) => completionRate(row) ?? -1,
        Cell: ({ row }) => {
          const rate = completionRate(row.original);
          return rate == null ? "-" : `${rate}%`;
        },
      },
      {
        accessorKey: "distinctClients",
        header: "Clients",
        size: 100,
        Cell: ({ row }) => formatCount(row.original.distinctClients),
      },
      {
        accessorKey: "courseOfferings",
        header: "Courses",
        size: 100,
        Cell: ({ row }) => formatCount(row.original.courseOfferings),
      },
      {
        accessorKey: "lastActivityDate",
        header: "Last Activity",
        size: 130,
        Cell: ({ row }) => formatDate(row.original.lastActivityDate),
      },
    ],
    []
  );

  const table = useMaterialReactTable({
    columns,
    data: rows,
    enableRowNumbers: true,
    rowNumberDisplayMode: "static",
    enablePagination: true,
    enableStickyHeader: true,
    enableGlobalFilter: true,
    positionGlobalFilter: "left",
    initialState: {
      density: "compact",
      showGlobalFilter: true,
      pagination: { pageIndex: 0, pageSize: 25 },
    },
    state: {
      isLoading: reportQuery.isLoading,
      showProgressBars: reportQuery.isFetching,
    },
    ...getMrtTableThemeProps({ maxHeight: "60vh" }),
    muiTableBodyRowProps: ({ row }) => ({ sx: getMrtBodyRowSx(row, { cursor: "default" }) }),
    renderDetailPanel: ({ row }) => (
      <Box sx={{ px: 2, py: 1.5, display: "flex", flexDirection: "column", gap: 1 }}>
        <Typography variant="caption" color="text.secondary" sx={{ textTransform: "uppercase" }}>
          Status breakdown
        </Typography>
        <StatusBreakdown breakdown={row.original.statusBreakdown} />
        <Typography variant="caption" color="text.secondary">
          {row.original.contactPerson
            ? `Contact: ${row.original.contactPerson}`
            : "No contact person"}
          {row.original.email ? ` · ${row.original.email}` : ""}
        </Typography>
      </Box>
    ),
  });

  return (
    <div className="flex flex-wrap mt-4 dashBody">
      <div className="w-full mb-12 px-4">
        <div className="relative flex flex-col min-w-0 break-words w-full mb-6 shadow-lg rounded bg-app-surface text-app-text">
          <div className="rounded-t mb-0 px-4 py-3 border-0">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <div className="flex items-center gap-2 font-semibold text-lg text-app-text">
                  <IoArrowBack className="text-xl cursor-pointer" onClick={() => navigate(-1)} />
                  College Admission Summary{" "}
                  <RefreshData refreshData={() => reportQuery.refetch()} />
                </div>
                {reportQuery.dataUpdatedAt > 0 && (
                  <Typography variant="caption" color="text.secondary">
                    Loaded {new Date(reportQuery.dataUpdatedAt).toLocaleTimeString()}. Use refresh
                    for the latest numbers.
                  </Typography>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outlined"
                  startIcon={
                    isExportingAdmissions ? <CircularProgress size={16} /> : <FaDownload />
                  }
                  disabled={isExportingAdmissions}
                  onClick={exportAllAdmissions}
                >
                  Export All Admissions
                </Button>
                <Button
                  variant="contained"
                  startIcon={<FaDownload />}
                  disabled={!rows.length}
                  onClick={() => downloadSummaryCsv(rows, totals)}
                >
                  Export CSV
                </Button>
              </div>
            </div>
          </div>
          <Divider />
          <div className="px-4 pt-4">
            <SummaryCards totals={totals} />
            {totals?.statusBreakdown && (
              <Box sx={{ mb: 2 }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ textTransform: "uppercase", display: "block", mb: 1 }}
                >
                  All admissions by status
                </Typography>
                <StatusBreakdown breakdown={totals.statusBreakdown} />
              </Box>
            )}
          </div>
          {reportQuery.isError && (
            <Typography color="error" sx={{ px: 2, pb: 2 }}>
              Could not load the admission summary. Refresh to try again.
            </Typography>
          )}
          <div className="block w-full overflow-x-auto px-2 pb-4">
            <MaterialReactTable table={table} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdmissionSummaryReport;
